import { useContext, type ReactNode } from 'react';
import { T } from 'gt-react';
import type { EntityCommentableType } from '@erp/rpc';
import { AuthContext } from 'src/context/auth.context';
import { CommentsCard } from '@/components/common/comments/CommentsCard';
import { useUsersInOrg } from '@/api/rpc/hooks/core/useUsersInOrg';
import {
  useCreateEntityComment,
  useEntityComments,
  useMentionableUsers,
  useUpdateEntityComment,
} from '@/api/rpc/hooks/core/useEntityComments';

export interface EntityNotesPanelProps {
  /** A `COMMENTABLE_ENTITY_TYPES` value, i.e. a registered `SystemEntity`. */
  entityType: EntityCommentableType;
  entityId: string;
  /** Defaults to "Notes"; `null` hides the heading (the host labels it). */
  heading?: ReactNode | null;
  /** Lets authors edit their own notes in place. */
  editable?: boolean;
  className?: string;
}

/**
 * Shared, human-written notes on any commentable record, with @mentions.
 *
 * Self-contained on purpose — it needs nothing but `{ entityType, entityId }`,
 * so a detail page, a slide-in, or a generated UI section can drop it in
 * without wiring data. Access follows the record: anyone who can read it can
 * read and add notes. See `backend/src/services/comments/README.md`.
 */
export function EntityNotesPanel({
  entityType,
  entityId,
  heading,
  editable = false,
  className,
}: EntityNotesPanelProps) {
  const ref = { commentableType: entityType, commentableId: entityId };
  const { currentUser } = useContext(AuthContext);
  const commentsQuery = useEntityComments(ref);
  const createComment = useCreateEntityComment(ref);
  const updateComment = useUpdateEntityComment(ref);
  const mentionableQuery = useMentionableUsers(ref);
  const orgUsersQuery = useUsersInOrg();

  // Readers of the record cover almost every author; the org list names
  // anyone who has since lost access.
  const userNameById = new Map(
    (orgUsersQuery.data ?? []).map((u) => [u.id, u.name]),
  );

  return (
    <CommentsCard
      // A new record gets a fresh composer, so a half-typed note never posts
      // to the next record a host switches to.
      key={`${entityType}:${entityId}`}
      comments={commentsQuery.data?.comments ?? []}
      isLoading={commentsQuery.isPending}
      // A failed background poll keeps the last good thread on screen.
      isError={commentsQuery.isError && !commentsQuery.data}
      isSubmitting={createComment.isPending}
      onSubmit={async (body) => {
        await createComment.mutateAsync({ ...ref, body });
      }}
      heading={
        heading === undefined ? (
          <T context="default heading for shared notes on a record">Notes</T>
        ) : (
          heading
        )
      }
      currentUserId={currentUser?.id}
      userNameById={userNameById}
      mentionableUsers={mentionableQuery.data}
      onUpdate={
        editable
          ? async (id, body) => {
              await updateComment.mutateAsync({ id, body });
            }
          : undefined
      }
      className={className}
    />
  );
}
